import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReceivedMatchingItem from "components/expert/received_matching_item";
import MatchingFilter from "components/expert/matching_filter";
import { useUser } from "util/context/user_context";
import { getDoneMatchApi } from "api/match";
import { parseRegionCsv, addressMatchesAny } from "util/constants/regions";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  blue700: "#1D4ED8",
};

const ITEMS_PER_PAGE = 5;
const PAGE_GROUP = 5;

function StatChip({ label, value, accent }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "14px 18px",
        background: "#fff",
        borderRadius: 12,
        border: `1px solid ${C.line}`,
        minWidth: 120,
        flex: "0 1 auto",
      }}
    >
      <div style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>{label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          background: "#F3F4F6",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 18, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "40%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "50%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: "#fff",
        borderRadius: 16,
        border: `1px dashed ${C.line}`,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 9999,
          background: C.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 34,
        }}
      >
        ✅
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
        매칭한 내역이 없어요
      </div>
      <div style={{ fontSize: 13, color: C.sub, textAlign: "center" }}>
        새로운 요청을 받으면 여기에서 진행 상황을 관리할 수 있어요.
      </div>
    </div>
  );
}

function SimplePagenation({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontSize: 14,
  };

  return (
    <div style={{ display: "flex", gap: 20, margin: "24px 0", justifyContent: "center", alignItems: "center" }}>
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        style={{ ...btnBase, cursor: canPrev ? "pointer" : "default", color: canPrev ? C.sub : "#c3c3c3" }}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>이전</span>
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {numbers.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: 9999,
                backgroundColor: active ? C.green : "transparent",
                color: active ? "#fff" : C.body,
                fontWeight: active ? 700 : 500,
                border: "none",
                transition: "all 0.15s ease",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        style={{ ...btnBase, cursor: canNext ? "pointer" : "default", color: canNext ? C.sub : "#c3c3c3" }}
      >
        <span>다음</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

const ExpertMatchingScreen = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const { userData } = useUser();

  const category = searchParams.get("category") || null;
  const regions = parseRegionCsv(
    searchParams.get("region") ??
      (userData?.region && userData.region !== "전국" ? userData.region : ""),
  );
  const regionUrlParam = searchParams.get("region") || "";
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1;
  });

  // 내가 매칭한 모든 요청을 한 번에 가져와 클라이언트 단에서 필터/페이지네이션
  useEffect(() => {
    if (!userData?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await getDoneMatchApi(userData.id);
        if (!cancelled) setAllItems(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setAllItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  // 카테고리 또는 지역 변경 시 첫 페이지로
  useEffect(() => {
    setPage(1);
  }, [category, regionUrlParam]);

  // 검색어 (농가 이름 / 내용 / 주소 / 분류)
  const [searchQ, setSearchQ] = useState("");

  // 카테고리 + 다중 지역 + 검색어 필터
  const filtered = useMemo(() => {
    let list = allItems;
    if (category) {
      list = list.filter((it) => {
        if (it?.selectList === category) return true;
        const names = it?.categoryListName ?? [];
        return names[0] === category;
      });
    }
    if (regions.length > 0) {
      list = list.filter((it) => addressMatchesAny(it?.address, regions));
    }
    const q = searchQ.trim().toLowerCase();
    if (q) {
      list = list.filter((it) => {
        const hay = [
          it?.name,
          it?.etc,
          it?.address,
          ...(Array.isArray(it?.categoryListName) ? it.categoryListName : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [allItems, category, regions, searchQ]);

  // 검색어 변경 시 첫 페이지로
  useEffect(() => {
    setPage(1);
  }, [searchQ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentItems = useMemo(
    () => filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page],
  );

  const stats = useMemo(() => {
    const total = allItems.length;
    const matched = allItems.filter((i) => String(i?.status) === "1").length;
    const completed = allItems.filter((i) => String(i?.status) === "2").length;
    return { total, matched, completed };
  }, [allItems]);

  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [totalPages, page]);

  useEffect(() => {
    sessionStorage.setItem(`page_${location.pathname}`, page);
    window.scrollTo({ top: 0 });
  }, [page, location.pathname]);

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            매칭 목록
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            내가 수락한 수리 요청과 진행 상황을 한눈에 확인하세요.
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatChip label="전체" value={stats.total} accent={C.ink} />
        <StatChip label="매칭완료" value={stats.matched} accent={C.blue700} />
        <StatChip label="완료" value={stats.completed} accent={C.greenDark} />
      </div>

      {/* Search */}
      <input
        type="text"
        value={searchQ}
        onChange={(e) => setSearchQ(e.target.value)}
        placeholder="농가 이름·내용·주소·분류로 검색"
        style={{
          height: 46,
          padding: "0 16px",
          borderRadius: 12,
          border: `1px solid ${C.line}`,
          background: "#fff",
          fontSize: 14,
          color: C.ink,
          outline: "none",
          fontFamily: "inherit",
          width: "100%",
          boxSizing: "border-box",
        }}
      />

      {/* Filter */}
      <MatchingFilter address={userData?.address} userRegion={userData?.region} />

      {/* List */}
      <div className="list" style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentItems.length > 0 ? (
          currentItems.map((item, index) => (
            <ReceivedMatchingItem key={item?.docId || index} item={item} />
          ))
        ) : (
          <EmptyState />
        )}
      </div>

      <SimplePagenation page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
};

export default ExpertMatchingScreen;
